// Copyright (C) 2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Card from 'antd/lib/card';
import Descriptions from 'antd/lib/descriptions';
import { MoreOutlined } from '@ant-design/icons';
import Dropdown from 'antd/lib/dropdown';

import { Job } from 'cvat-core-wrapper';
import { useCardHeightHOC } from 'utils/hooks';
import Preview from 'components/common/preview';
import JobActionsMenu from 'components/job-item/job-actions-menu';
import { CombinedState } from 'reducers';
import { useDispatch } from 'react-redux';
import { resetErrors as resetNotificationsErrors } from 'actions/notification-actions';

const useCardHeight = useCardHeightHOC({
    containerClassName: 'cvat-jobs-page',
    siblingClassNames: ['cvat-jobs-page-pagination', 'cvat-jobs-page-top-bar'],
    paddings: 64,
    minHeight: 200,
    numberOfRows: 3,
});

interface Props {
    job: Job;
}

async function fetchAllPaginated(dispatchFunc, key, dispatch) {
    const allItems = [];
    let page = 1;

    while (true) {
        try {
            const result = await dispatch(dispatchFunc({ page })) as any;
            if (!result || result.length === 0) break;

            allItems.push(...result);
            page++;
        } catch (err: any) {
            if (err?.message?.includes('Invalid page')) {
                console.warn(`[Silent] Reached end of ${key} pages.`);
                dispatch(resetNotificationsErrors());
                break;
            } else {
                console.warn(`Failed to fetch ${key}:`, err);
                break;
            }
        }
    }

    return allItems;
}

function JobCardComponent(props: Props): JSX.Element {
    const dispatch = useDispatch();
    const { job } = props;
    const taskFound = useSelector((state: CombinedState) => {
        return state.tasks.current.some((t) => t.id === job.taskId);
    });


    const taskName = useSelector((state: CombinedState) => {
        const task = state.tasks.current.find((t) => t.id === job.taskId);
        return task ? task.name : '';
    });    

    const deletes = useSelector((state: CombinedState) => state.jobs.activities.deletes);
    const deleted = job.id in deletes ? deletes[job.id] === true : false;

    const history = useHistory();
    const height = useCardHeight();
    const onClick = (event: React.MouseEvent): void => {
        const url = `/tasks/${job.taskId}/jobs/${job.id}`;
        if (event.ctrlKey) {
            window.open(url, '_blank', 'noopener noreferrer');
        } else {
            history.push(url);
        }
    };

    const style: React.CSSProperties = {};
    if (deleted) {
        style.pointerEvents = 'none';
        style.opacity = 0.5;
    }

    // Apply light blue background if the job is a quality_check job
    if (job.type?.toLowerCase() === 'quality_check') {
    style.backgroundColor = '#1DA0DE';
    }


    return (
        <Card
            style={{ ...style, height }}
            className='cvat-job-page-list-item'
            cover={(
                <>
                    <Preview
                        job={job}
                        onClick={onClick}
                        loadingClassName='cvat-job-item-loading-preview'
                        emptyPreviewClassName='cvat-job-item-empty-preview'
                        previewWrapperClassName='cvat-jobs-page-job-item-card-preview-wrapper'
                        previewClassName='cvat-jobs-page-job-item-card-preview'
                    />
                    <div className='cvat-job-page-list-item-id'>
                        ID:
                        {` ${job.id}`}
                    </div>
                    <div className='cvat-job-page-list-item-dimension'>{job.dimension.toUpperCase()}</div>
                </>
            )}
            hoverable
        >
            <Descriptions column={1} size='small'>
                <Descriptions.Item label='Stage and state'>{`${job.stage} ${job.state}`}</Descriptions.Item>
                <Descriptions.Item label='Frames'>{job.stopFrame - job.startFrame + 1}</Descriptions.Item>
                { job.assignee ? (
                    <Descriptions.Item label='Assignee'>{job.assignee.username}</Descriptions.Item>
                ) : <Descriptions.Item label='Assignee'> </Descriptions.Item>}
                <Descriptions.Item label='Task'>{taskName}</Descriptions.Item>  {/* <-- move this inside */}
            </Descriptions>

            <Dropdown
                trigger={['click']}
                destroyPopupOnHide
                overlay={(<JobActionsMenu job={job} />)}
            >
                <MoreOutlined className='cvat-job-card-more-button' />
            </Dropdown>
        </Card>
    );
}

export default React.memo(JobCardComponent);
